import { z } from "zod";

/**
 * Gets a property reference with a given number of prefixes in the provided
 * order. The reference is always dot-separated.
 *
 * Usually used for UI that does accessing on a domain model, like filters or table columns/cells
 * @param property the property path reference to something in a domain model
 * @param prefix optional prefixes to add to the property
 * @returns dot-separated property reference
 */
export const getPropertyReference = (
  property: string,
  prefix?: string[]
): string => {
  if (!property) return "";

  if (!prefix || prefix.length === 0) {
    return property;
  }

  return prefix.join(".") + "." + property;
};

/**
 * Traverses a Zod schema to validate a property path and return the
 * dot-separated path without any special syntaxes
 * @param schema Zod object schema to traverse. Array access is supported via special syntax "[]" e.g. obj.arrProp1.[].prop1
 * @param propertyPath dot-separated property path to validate with special access syntaxes
 * @returns dot-separated property path if valid, or empty string if invalid
 */
function traverseSchemaPath<T extends z.ZodObject<any, any, any>>(
  schema: T,
  propertyPath: string
): string {
  if (!propertyPath) return "";

  const pathParts = propertyPath.split(".");
  let currentSchema: any = schema;
  const validatedPath: string[] = [];

  for (const part of pathParts) {
    // to access arrays in the path, we use special syntax "[]"
    if (part === "[]") {
      if (currentSchema instanceof z.ZodArray) {
        currentSchema = currentSchema.element;
        // don't add `[]` to the validated path since it's just syntax
        continue;
      } else {
        // invalid access, trying to access array on non-array schema
        return "";
      }
    }

    // check if the part exists in the current schema
    if (currentSchema?.shape && part in currentSchema.shape) {
      validatedPath.push(part);
      currentSchema = currentSchema.shape[part];
    } else {
      // invalid access
      return "";
    }

    // if it's not an object or array, we can't go deeper
    if (
      !(currentSchema instanceof z.ZodObject) &&
      !(currentSchema instanceof z.ZodArray)
    ) {
      break;
    }
  }

  // the fully traversed dot-separated path
  return validatedPath.join(".");
}

/**
 * Allows typing a value that could be a property access path in a Zod object
 * schema. Does not support array access.
 */
export type SchemaPropertyPathType<T> =
  T extends z.ZodObject<infer Shape, any, any>
    ? {
        [K in keyof Shape]: Shape[K] extends z.ZodObject<any, any, any>
          ? K | `${K & string}.${SchemaPropertyPathType<Shape[K]> & string}`
          : K;
      }[keyof Shape]
    : never;

/**
 * Allows typing a value that could be a property access reference in a Zod
 * object schema, with special syntax for arrays
 */
export type FlexibleSchemaPropertyPathType<T> =
  T extends z.ZodObject<infer Shape, any, any>
    ?
        | Extract<keyof Shape, string>
        | `${Extract<keyof Shape, string>}.${string}`
        | `${Extract<keyof Shape, string>}.[].${string}`
    : never;

/**
 * Verifies if a property reference exists in the provided schema during runtime
 * and returns a dot-separated property reference if it's valid.
 *
 * Usually used for UI that does accessing on a domain model, like filters or table columns/cells
 * @param schema Zod object schema to traverse. Array access is supported via special syntax "[]" e.g. obj.arrProp1.[].prop1
 * @param property dot-separated property path to validate with special access syntaxes
 * @param prefix optional prefixes to add to the property
 * @returns dot-separated property reference
 */
export const getSchemaPropertyReference = <
  T extends z.ZodObject<any, any, any>,
>(
  schema: T,
  property: FlexibleSchemaPropertyPathType<T>,
  prefix?: string[]
): string => {
  const validatedPath = traverseSchemaPath(schema, property);

  if (!validatedPath) {
    // property path doesn't exist in schema
    return "";
  }

  return getPropertyReference(validatedPath, prefix);
};
